// Per-brand page content for the enterprise directory.
//
// ANTI-DOORWAY RULE (docs/research/enterprise-brand-directory.md): a brand only gets
// a page when it has genuinely brand-specific substance here — real product families,
// real buying questions. Template-swapped filler is what Google's spam policy targets,
// and one algorithmic hit poisons the whole domain. If you cannot write four distinct
// supply items and four distinct FAQs for a brand, leave it hub-listed.
//
// SOURCING RULE: descriptive "we resell {Brand}" only (see brands.ts for Claude and OpenAI wording). No conferred titles
// (Authorised Partner / Certified Reseller) unless the certificate is held, and the
// distributor is never named. No prices, no stock claims, no lead times.

export interface BrandSupplyItem {
  title: string;
  detail: string;
}

export interface BrandFaq {
  question: string;
  answer: string;
}

export interface BrandPageContent {
  slug: string;
  brand: string;
  categoryLabel: string; // hero eyebrow suffix
  h1: string;
  intro: string;
  supply: BrandSupplyItem[];
  /** The use-case bridge into our own services — only where genuinely honest. */
  bridge: { heading: string; body: string; href: string; cta: string };
  faqs: BrandFaq[];
  /**
   * Optional pre-purchase conditions that are NOT a supply item — currently only
   * NVIDIA's export-control documentation. Deliberately optional so the other ten
   * brand pages are untouched, and deliberately separate from `supply` because it
   * describes what a buyer must do before we quote, not what we sell.
   *
   * ACCURACY GUARD: this block says what WE require before releasing pricing. It
   * must never state what a regulation requires, cite an ECCN, or name a
   * jurisdiction's rules — that is a legal claim and not ours to make.
   */
  compliance?: {
    heading: string;
    body: string;
    points: string[];
    cta: { href: string; label: string };
  };
  trademark: string;
  /** Sibling slugs for the related strip. */
  related: string[];
}

export const brandPages: Record<string, BrandPageContent> = {
  "microsoft-malaysia": {
    slug: "microsoft-malaysia",
    brand: "Microsoft",
    categoryLabel: "Software licences",
    h1: "Authorised Microsoft Reseller Malaysia — licensing, CSP & renewals",
    intro:
      "We are an authorised Microsoft reseller in Malaysia — Microsoft 365, Windows and server licensing, Azure consumption and GitHub Enterprise — quoted in RM with SST stated and MyInvois e-invoicing handled.",
    supply: [
      {
        title: "Microsoft 365 subscriptions",
        detail:
          "Business and Enterprise plans licensed per user, sized against who actually needs which apps — most estates are paying for a tier above what half the seats use.",
      },
      {
        title: "Windows & server licensing",
        detail:
          "Windows Server, SQL Server and client access licensing, including the core-count and CAL arithmetic that decides the invoice.",
      },
      {
        title: "Azure consumption",
        detail:
          "Azure subscriptions billed in RM against your usage, with cost review as part of the renewal conversation rather than a surprise at year end.",
      },
      {
        title: "Renewals, true-ups & seat changes",
        detail:
          "Seat additions mid-term, annual true-ups and renewal co-terming so one date covers the estate rather than a scatter of anniversaries.",
      },
    ],
    bridge: {
      heading: "Rolling out Copilot or AI across the estate?",
      body: "Licensing is the easy half. We deploy and govern AI inside Malaysian businesses in production — the licence quote and the rollout plan can come from the same team.",
      href: "/services/ai-solutions/",
      cta: "Work with an AI agency in Malaysia",
    },
    faqs: [
      {
        question: "Can I buy Microsoft 365 licences in Malaysia through Anchor Sprint?",
        answer:
          "Yes. We are an authorised Microsoft reseller in Malaysia and supply Microsoft 365 Business and Enterprise subscriptions, quoted in RM with SST stated. Send your seat count and which plans you need through the enquiry form.",
      },
      {
        question: "How is Azure billed if we buy through you?",
        answer:
          "Azure is billed on consumption. We quote and invoice in RM against your actual usage, and review the spend at renewal rather than leaving it to surface at year end.",
      },
      {
        question: "Can you handle a mid-term seat increase?",
        answer:
          "Yes. Seats can normally be added mid-term and co-termed to your existing renewal date, so the estate stays on one anniversary rather than fragmenting.",
      },
      {
        question: "Do you help work out which licence tier we actually need?",
        answer:
          "Yes, and it is usually where the money is. Most estates are licensed a tier above what a large share of seats use. Tell us what your teams actually do and we will size the quote to that, not to the safest upsell.",
      },
    ],
    trademark:
      "Anchor Sprint is an authorised Microsoft reseller in Malaysia, including GitHub Enterprise. Microsoft, Microsoft 365, Azure and GitHub are trademarks of Microsoft Corporation; this page does not imply endorsement by Microsoft.",
    related: ["adobe-malaysia", "github-malaysia", "red-hat-malaysia"],
  },

  "red-hat-malaysia": {
    slug: "red-hat-malaysia",
    brand: "Red Hat",
    categoryLabel: "Software licences",
    h1: "Authorised Red Hat Reseller Malaysia — RHEL, OpenShift & subscriptions",
    intro:
      "We are an authorised Red Hat reseller in Malaysia — Red Hat Enterprise Linux, OpenShift and Ansible subscriptions — quoted in RM with SST stated and renewal dates tracked.",
    supply: [
      {
        title: "Red Hat Enterprise Linux",
        detail:
          "RHEL subscriptions counted the way Red Hat counts them — by socket-pair or virtual instance — which is where most estates over- or under-license without realising.",
      },
      {
        title: "OpenShift",
        detail:
          "Container platform subscriptions sized on the node and core counts you will actually run, with the support tier stated on the quote.",
      },
      {
        title: "Ansible Automation Platform",
        detail:
          "Automation licensing scoped to managed node counts, quoted alongside the platform it automates.",
      },
      {
        title: "Renewals & entitlement review",
        detail:
          "Subscriptions co-termed onto one date, with an entitlement review before renewal so you are not repurchasing capacity that is sitting idle.",
      },
    ],
    bridge: {
      heading: "Running AI workloads on OpenShift?",
      body: "Container platforms are increasingly where inference workloads land. We deploy and operate those systems in production, so the subscription quote and the platform design can come from the same conversation.",
      href: "/services/ai-solutions/",
      cta: "Work with an AI agency in Malaysia",
    },
    faqs: [
      {
        question: "Can I buy Red Hat subscriptions in Malaysia through Anchor Sprint?",
        answer:
          "Yes. We are an authorised Red Hat reseller in Malaysia — RHEL, OpenShift and Ansible Automation Platform — quoted in RM with SST stated.",
      },
      {
        question: "How is RHEL licensed — per server or per core?",
        answer:
          "RHEL is subscription-based and counted by socket-pair or virtual instance depending on the offering, which is where estates commonly miscount. Send your host and VM inventory and we will size it correctly on the quotation.",
      },
      {
        question: "Can you review whether we are over-subscribed before renewal?",
        answer:
          "Yes, and it is worth doing. Send your current entitlement and deployment inventory and we will identify what is genuinely in use, so the renewal reflects the estate rather than last year's plan.",
      },
      {
        question: "Do you support the platform as well as sell the subscription?",
        answer:
          "Yes. Support is included through the subscription, and where you want implementation or operational help — cluster design, migration, automation — that can be scoped as an engagement alongside the licensing.",
      },
    ],
    trademark:
      "Anchor Sprint is an authorised Red Hat reseller in Malaysia. Red Hat, RHEL, OpenShift and Ansible are trademarks of Red Hat, Inc.; this page does not imply endorsement by Red Hat.",
    related: ["microsoft-malaysia", "dell-technologies-malaysia", "sonarqube-malaysia"],
  },

  "nvidia-malaysia": {
    slug: "nvidia-malaysia",
    brand: "NVIDIA",
    categoryLabel: "AI & compute",
    h1: "NVIDIA Supplier Malaysia — GPUs for AI training & inference",
    intro:
      "We supply NVIDIA in Malaysia through authorised distribution — data-centre GPUs, RTX professional cards, AI Enterprise licensing and the networking around them — quoted in RM with SST stated and MyInvois e-invoicing handled. We are a reseller sourcing through authorised Malaysian distribution, not a distributor.",
    supply: [
      {
        title: "Data-centre GPUs (H100, H200, B200, B300, GB300, L40S)",
        detail:
          "The accelerators behind training and high-volume inference, as loose cards or in HGX and DGX platforms. Supply of advanced parts is allocation-based and subject to export controls, so availability is confirmed against your configuration at quotation rather than quoted from a list — and signed end-user documentation comes before pricing.",
      },
      {
        title: "RTX professional GPUs",
        detail:
          "Workstation cards for teams doing model development, fine-tuning and visualisation locally. Often the right answer before a data-centre purchase is justified — and a far shorter procurement conversation.",
      },
      {
        title: "NVIDIA AI Enterprise licensing",
        detail:
          "The supported software layer — frameworks, inference microservices and security patching — licensed per GPU or per node. It is a separate subscription from the hardware, bundled with some systems and not others, which is where most first budgets are wrong.",
      },
      {
        title: "Networking & certified systems",
        detail:
          "Spectrum Ethernet, Quantum InfiniBand and BlueField DPUs, plus NVIDIA-Certified servers from Dell and Lenovo. At multi-GPU scale the interconnect, not the GPU, is usually what caps throughput.",
      },
    ],
    bridge: {
      heading: "Looking for DGX Spark specifically?",
      body: "The GB10 desktop supercomputer has its own page, covering the NVIDIA Founders Edition and every OEM build — ASUS Ascent GX10, Dell Pro Max with GB10, HP ZGX Nano, Lenovo ThinkStation PGX, Acer Veriton GN100 and the rest — with what actually differs between them and when a Spark is the wrong purchase.",
      href: "/enterprise/dgx-spark-malaysia/",
      cta: "See DGX Spark in Malaysia",
    },
    faqs: [
      {
        question: "Can I buy NVIDIA GPUs in Malaysia through Anchor Sprint?",
        answer:
          "Yes. We supply NVIDIA in Malaysia through authorised distribution — data-centre GPUs, RTX professional cards, NVIDIA-Certified systems and AI Enterprise licensing — quoted in Ringgit with SST stated and MyInvois e-invoicing handled.",
      },
      {
        question: "Are you an NVIDIA distributor in Malaysia?",
        answer:
          "No. We are a reseller and we source through authorised Malaysian distribution. The practical difference for you is none at the point of purchase: you get a Malaysian quotation in RM, local PO terms, SST treatment stated and a MyInvois e-invoice. Anyone claiming to be NVIDIA's distributor in Malaysia is describing a different relationship from ours.",
      },
      {
        question: "Can I use GeForce cards in a server instead of data-centre GPUs?",
        answer:
          "For a workstation under a desk, yes. In a data centre, NVIDIA's driver licence terms restrict GeForce deployment, which is why the data-centre and RTX professional lines exist. If your workload is a handful of researchers fine-tuning models, an RTX workstation is usually the honest recommendation; if it is shared multi-tenant inference, it is not.",
      },
      {
        question: "How much does an H100 cost in Malaysia?",
        answer:
          "We do not publish prices for NVIDIA hardware. Data-centre GPU pricing moves with configuration, allocation and the system it ships in, so it is quotation-based. Send your workload, GPU count and whether you need the server around it through the enquiry form and we return a quotation in Ringgit. For data-centre parts the end-use questions come first — we cannot release pricing before the documentation is in hand.",
      },
      {
        question: "Why do you need end-user documentation for an H200 or a B300?",
        answer:
          "Advanced data-centre accelerators are subject to export controls, and signed end-user documentation is required before pricing can be released. The exact form depends on the platform the order is configured against, so we gather the underlying information first — who the buying entity is, what the hardware will actually run, where it will physically sit, whether anyone else gets access to it, and who is paying. RTX professional cards, AI Enterprise licensing and networking are quoted without any of this.",
      },
      {
        question: "How long does export screening add to a GPU order in Malaysia?",
        answer:
          "Answering the questions takes a sitting if you have your registration details, deployment site and workload to hand — there is one form and you complete it once. The screening itself then adds working days, not weeks, before a quotation can be released. What actually causes delay is a vague end use: an answer like \"AI\" or \"GPU cluster\" is not specific enough for the documentation and sends the whole thing back a step, which is why our form asks which model, which operation, at what scale and for whom, rather than asking you to describe it in one box.",
      },
    ],
    compliance: {
      heading: "Before we can quote advanced GPUs",
      body: "Data-centre accelerators in this class are subject to export controls. Signed end-user documentation is required before we release pricing, and the exact form depends on the platform we configure against — so we gather the underlying information once, up front, rather than sending you the wrong form. RTX professional cards, AI Enterprise licensing and networking are quoted without any of it.",
      points: [
        "Who the buying entity is — registered name, registration number and registered office",
        "What the hardware will actually run — the model, the operation, the scale, and who consumes the output",
        "Where it will physically sit, and whether the ship-to address is your own site",
        "Whether anyone else gets access to it, through resale or as-a-Service",
        "Who is funding and paying for the purchase",
      ],
      cta: { href: "/enterprise/nvidia-malaysia/enquiry/", label: "Start the GPU enquiry" },
    },
    trademark:
      "NVIDIA, H100, H200, B200, B300, GB300, HGX, DGX, L40S, RTX, Spectrum, Quantum, BlueField and NVIDIA AI Enterprise are trademarks of NVIDIA Corporation. Anchor Sprint is an authorised NVIDIA DGX Spark reseller in Malaysia and supplies other NVIDIA products through authorised Malaysian distribution; this page does not imply endorsement by NVIDIA Corporation.",
    related: ["dgx-spark-malaysia", "dell-technologies-malaysia", "lenovo-malaysia"],
  },
};

export const brandPageSlugs = Object.keys(brandPages);
